import React, { ComponentProps } from "react";
import { useSelector } from "react-redux";
import styles from "./PrintOrderInfo.module.scss";
import Logo from "../../../UI/Logo/LogoBW";
import NBSQRCode from "../../../UI/NBSQRCode/NBSQRCode";
import { selectActiveLicense } from "../../../../store/auth-slice";
import { printStyles } from "./printStyles";
import {
  isFriendPickup,
  isPickup,
  isVolunteerDelivery,
} from "../../../../utils/deliveryType";

interface Props extends ComponentProps<any> {}

export type Ref = HTMLDivElement;

const PrintOrderInfo = React.forwardRef<Ref, Props>((props, ref) => {
  const { order } = props;
  const activeLicense = useSelector(selectActiveLicense);

  // Calculate total number of items
  const totalItems =
    order.items?.reduce((total: number, item: any) => {
      return total + (item.quantity || 0);
    }, 0) || 0;

  const needsDeliveryQR =
    order.userDeliveryResponse && isVolunteerDelivery(order);

  const isPickupOrder = isPickup(order);

  return (
    <div ref={ref} className={styles.PrintWrapper} style={printStyles.wrapper}>
      {/* Logo Section */}
      <div style={printStyles.logoSection}>
        <Logo />
      </div>

      <hr style={printStyles.divider} />

      {/* Order Header Section - DETALJI REZERVACIJE */}
      <div style={printStyles.orderHeaderSection}>
        <div style={printStyles.orderHeaderTitle}>DETALJI REZERVACIJE</div>

        {/* Flex Row: Inverted Badge + Stats */}
        <div style={printStyles.orderHeaderRow}>
          {/* Inverted Order Badge */}
          <div style={printStyles.orderBadge}>#{order.id}</div>

          {/* Order Stats */}
          <div style={printStyles.orderStats}>
            <div style={printStyles.orderStatRow}>
              <span style={printStyles.label}>Broj stavki: </span>
              <strong style={printStyles.value}>{totalItems}</strong>
            </div>
            <div style={printStyles.orderStatRow}>
              <span style={printStyles.label}>Ukupan iznos: </span>
              <strong style={printStyles.value}>{order.totalPrice} RSD</strong>
            </div>
          </div>
        </div>
      </div>

      <hr style={printStyles.divider} />

      {/* Customer Information Section */}
      <div style={printStyles.section}>
        <div style={printStyles.sectionTitle}>NARUČILAC</div>

        <div style={printStyles.fieldRow}>
          <span style={printStyles.label}>Ime i prezime: </span>
          <strong style={printStyles.value}>
            {order.firstName} {order.secondName}
          </strong>
        </div>

        <div style={printStyles.fieldRow}>
          <span style={printStyles.label}>Broj telefona: </span>
          <strong style={printStyles.value}>
            {order.userPhoneNumberForOrder}
          </strong>
        </div>

        {order.userAddressResponse && (
          <div style={printStyles.fieldRow}>
            <span style={printStyles.label}>Adresa: </span>
            <strong style={printStyles.value}>
              {order.userAddressResponse.name}{" "}
              {order.userAddressResponse.number}
              {order.userAddressResponse.apartment &&
                `/${order.userAddressResponse.apartment}`}
              , {order.userAddressResponse.city}
            </strong>
          </div>
        )}
      </div>

      <hr style={printStyles.divider} />

      {/* Pharmacist Information Section - PRIPREMLJENO SA PAŽNJOM */}
      {activeLicense && (
        <>
          <div style={printStyles.section}>
            <div style={printStyles.pharmacistThankYou}>
              Dragi korisniče, hvala Vam na poverenju. Vašu rezervaciju je sa
              posebnom pažnjom pregledao i za Vas spakovao diplomirani
              farmaceut:
            </div>

            <div style={printStyles.fieldRow}>
              <strong style={printStyles.value}>
                {activeLicense.licenseUserFirstName}{" "}
                {activeLicense.licenseUserSecondName}
              </strong>
            </div>

            <div style={printStyles.fieldRow}>
              <span style={printStyles.label}>Broj licence: </span>
              <strong style={printStyles.value}>
                {activeLicense.licenseCode}
              </strong>
            </div>

            <div style={printStyles.pharmacistDisclaimer}>
              Radi poštovanja Vaše privatnosti, fiskalni račun nalazi se u
              sigurnosnoj kesi.
            </div>
          </div>

          <hr style={printStyles.divider} />
        </>
      )}

      {/* Pickup/Delivery Section with Inline QR */}
      {needsDeliveryQR && order.userDeliveryResponse && (
        <>
          <div style={printStyles.section}>
            <div style={printStyles.sectionTitle}>
              OVLAŠĆENO LICE ZA PREUZIMANJE
            </div>

            {/* Flex Row: Contact Info + QR Code */}
            <div style={printStyles.pickupFlexRow}>
              {/* Left: Contact Info */}
              <div style={printStyles.pickupInfo}>
                <div style={printStyles.fieldRow}>
                  <span style={printStyles.label}>Ime i prezime: </span>
                  <strong style={printStyles.value}>
                    {order.userDeliveryResponse.firstName}{" "}
                    {order.userDeliveryResponse.secondName}
                  </strong>
                </div>

                <div style={printStyles.fieldRow}>
                  <span style={printStyles.label}>Broj telefona: </span>
                  <strong style={printStyles.value}>
                    {order.userDeliveryResponse.phoneNumber}
                  </strong>
                </div>

                {order.orderVerificationCode && (
                  <div style={printStyles.fieldRow}>
                    <span style={printStyles.label}>
                      Verifikacioni kod za preuzimanje:{" "}
                    </span>
                    <div style={printStyles.verificationCode}>
                      {order.orderVerificationCode}
                    </div>
                  </div>
                )}
              </div>

              {/* Right: QR Code with label above */}
              <div style={printStyles.qrWithLabel}>
                <div style={printStyles.qrLabel}>
                  IPS Skenirajte za plaćanje
                </div>
                <div style={printStyles.qrContainer}>
                  <NBSQRCode order={order} size={80} />
                </div>
              </div>
            </div>
          </div>

          <hr style={printStyles.divider} />
        </>
      )}

      {/* Volunteer Information (for volunteer-delivery orders without QR) */}
      {isVolunteerDelivery(order) && !needsDeliveryQR && (
        <>
          <div style={printStyles.section}>
            <div style={printStyles.sectionTitle}>PODACI O VOLONTERU</div>

            <div style={printStyles.fieldRow}>
              <span style={printStyles.label}>Volonter još nije dodeljen.</span>
            </div>

            {order.orderVerificationCode && (
              <div style={printStyles.fieldRow}>
                <span style={printStyles.label}>
                  Verifikacioni kod za preuzimanje:{" "}
                </span>
                <div style={printStyles.verificationCode}>
                  {order.orderVerificationCode}
                </div>
              </div>
            )}
          </div>

          <hr style={printStyles.divider} />
        </>
      )}

      {/* Pickup Instructions for Non-Delivery Orders */}
      {isPickupOrder && (
        <>
          <div style={printStyles.pickupInstructionsBox}>
            <div style={{ fontWeight: "bold", marginBottom: "6px" }}>
              Instrukcije za preuzimanje:
            </div>
            {isFriendPickup(order) ? (
              <div>
                • Osoba koja preuzima treba da ima ovaj ispis
                <br />
                • Potrebna je validna identifikacija
                <br />• Plaćanje pri preuzimanju:{" "}
                <strong>{order.totalPrice} RSD</strong>
              </div>
            ) : (
              <div>
                • Porudžbina se plaća pri preuzimanju u apoteci
                <br />
                • Molimo da ponesete validnu identifikaciju
                <br />• Ukupan iznos za plaćanje:{" "}
                <strong>{order.totalPrice} RSD</strong>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
});

export default PrintOrderInfo;
